import { effect, inject, Injectable, signal } from '@angular/core';
import { ApiDataAccess, getValue, Query, QueryBuilder } from '@softline/core';
import { SalesStatisticsGridDataSource } from '../grid/sales-statistics-grid.data-source';
import { applyActiveFilters } from '../helpers/apply-active-filters.function';
import {
  addDateFilter,
  addDbPercentColumn,
  addPeriodColumns,
  addPeriodFilters,
  addValueColumns,
  splitByPeriod,
  valFromRow,
} from '../helpers/document-type-query.helpers';
import { ApiRow } from '../types/api-row';
import { CustomerDrillRow } from '../types/chart-row';
import { SalesStatisticsQueryService } from './sales-statistics-query.service';
import { CUSTOMER_DRILL, DrillLevel } from '../helpers/drill-levels';

interface LoadResult {
  data: ApiRow[];
}

@Injectable()
export class CustomerDrillChartService {
  queryService = inject(SalesStatisticsQueryService);
  private dataAccess = inject(ApiDataAccess);

  private cache = new Map<string, CustomerDrillRow[]>();

  readonly customerDrillRows = signal<CustomerDrillRow[]>([]);
  readonly loading = signal(false);
  readonly hasError = signal(false);
  lastParamsKey = '';

  constructor() {
    let lastInvalidationKey: string | null = null;
    effect(() => {
      const key = JSON.stringify([
        this.queryService.activeFilters(),
        this.queryService.dateRange(),
        this.queryService.referencePeriod(),
        this.queryService.documentType(),
        this.queryService.statisticType(),
        this.queryService.refreshTick(),
      ]);
      if (lastInvalidationKey === null) {
        lastInvalidationKey = key;
        return;
      }
      if (lastInvalidationKey === key) return;
      lastInvalidationKey = key;
      this.cache.clear();
      this.lastParamsKey = '';
    });
  }

  async loadMainGroups(
    from: string,
    to: string,
    referencePeriod: { from: string; to: string } | null,
    documentType = 'INVOICE',
    statisticType = 'TOTAL',
    signal?: AbortSignal,
    level: DrillLevel = CUSTOMER_DRILL[0],
  ): Promise<CustomerDrillRow[]> {
    const cols = { code: level.code, name: level.name };
    const cacheKey = JSON.stringify({ from, to, referencePeriod, documentType, statisticType, level: level.key, filters: this.queryService.activeFilters() });

    const ds = new SalesStatisticsGridDataSource(this.dataAccess, documentType, statisticType);

    const base = new QueryBuilder<ApiRow>()
      .start(0)
      .end(100)
      .column(cols.code, { usage: 'group' })
      .column(cols.name, { usage: 'group' });
    base.sort(cols.name, 'ASC');
    applyActiveFilters(base, this.queryService);
    addValueColumns(base);
    addDbPercentColumn(base);

    const cached = this.cache.get(cacheKey);
    if (cached) return cached;

    let currentData: ApiRow[];
    let refData: ApiRow[];

    if (referencePeriod) {
      const builder = base.copy();
      addPeriodColumns(builder);
      addPeriodFilters(builder, { from, to }, referencePeriod);
      const result = await ds.loadAbortable(
        { query: builder.build() as Query<object> },
        signal,
      ) as LoadResult;
      const split = splitByPeriod(result?.data ?? []);
      currentData = split.current;
      refData = split.previous;
    } else {
      const builder = base.copy();
      addDateFilter(builder, from, to);
      const result = await ds.loadAbortable(
        { query: builder.build() as Query<object> },
        signal,
      ) as LoadResult | null;
      currentData = result?.data ?? [];
      refData = [];
    }
    const keyOf = (r: ApiRow): string =>
      String(getValue(r, cols.code) ?? '');
    const currentByKey = new Map(currentData.map((r) => [keyOf(r), r]));
    const refByKey = new Map(refData.map((r) => [keyOf(r), r]));
    const allKeys = [...new Set([...currentByKey.keys(), ...refByKey.keys()])];

    const rows: CustomerDrillRow[] = allKeys
      .map((code) => {
        const o = currentByKey.get(code);
        const ref = refByKey.get(code);
        const net = valFromRow(o, 'net');
        const cost = valFromRow(o, 'cost');
        const db = valFromRow(o, 'contribution_margin');
        const dbPct = valFromRow(o, 'contribution_margin_percent');
        const refNet = referencePeriod ? valFromRow(ref, 'net') : net;
        const refDb = referencePeriod ? valFromRow(ref, 'contribution_margin') : db;
        const refDbPct = referencePeriod
          ? valFromRow(ref, 'contribution_margin_percent')
          : dbPct;
        const groupName = getValue(o ?? ref, cols.name);
        return {
          field: groupName,
          name: groupName,
          code,
          revenue: net,
          wes: cost,
          db,
          dbPercent: dbPct,
          // Stock has no customer dimension — the backend cannot resolve a
          // Lagerwert per customer/customer group, so this chart never shows one.
          inventoryValue: 0,
          referenceRevenue: refNet,
          referenceDb: refDb,
          referenceDbPercent: refDbPct,
          referenceInventoryValue: 0,
          minDbPercent: Math.min(dbPct, refDbPct),
          dbPercentDeltaPos: refDbPct < dbPct ? dbPct - refDbPct : 0,
          dbPercentDeltaNeg: refDbPct > dbPct ? refDbPct - dbPct : 0,
          minRevenue: Math.min(net, refNet),
          dbPos: refNet < net ? net - refNet : 0,
          dbNeg: refNet > net ? refNet - net : 0,
          minDb: Math.min(db, refDb),
          dbDeltaPos: refDb < db ? db - refDb : 0,
          dbDeltaNeg: refDb > db ? refDb - db : 0,
          minInventoryValue: 0,
          inventoryDeltaPos: 0,
          inventoryDeltaNeg: 0,
        };
      })
      .sort((a: CustomerDrillRow, b: CustomerDrillRow) => b.revenue - a.revenue)
      .slice(0, 15);
    this.cache.set(cacheKey, rows);
    return rows;
  }
}
